import axios from "axios";

import { API } from "../constants/api";

/**
 * Shared helpers used by every place that reports an HTTP failure to Sentry:
 * the axios response interceptor, the TanStack Query caches, and `beforeSend`
 * in `sentry.ts`, which is the SDK's only filter that sees the error object
 * instead of just its message.
 *
 * Both concerns live here so the rules cannot drift apart again: until now the
 * same intent was duplicated in three lists that each failed to match the real
 * axios messages.
 */

/** Canonical UUID (with or without dashes), case-insensitive. */
const UUID_SEGMENT =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/** Purely numeric ids (tenant report folios are numeric). */
const NUMERIC_SEGMENT = /^\d+$/;

/** Long opaque hex tokens (verification tokens and similar). */
const LONG_HEX_SEGMENT = /^[0-9a-f]{16,}$/i;

/**
 * Short opaque codes: 6+ alphanumerics mixing at least one letter and at least
 * one digit (`ABC123`, the 6-character code of `/get-full-token/:code`).
 *
 * Requiring both a letter and a digit is what keeps this from eating literal
 * segments: every static segment in this API is either a word, a dash-separated
 * slug or a version (`organization`, `resend-email`, `base-clauses`, `v1`), and
 * none of them mixes letters with digits. Verified against every literal
 * segment in `src/api`.
 */
const OPAQUE_CODE_SEGMENT = /^(?=[a-z0-9]*\d)(?=[a-z0-9]*[a-z])[a-z0-9]{6,}$/i;

/** `https://api.example.com` of an absolute URL. */
const ORIGIN_PREFIX = /^[a-z][a-z0-9+.-]*:\/\/[^/]+/i;

/** The `https://` of an origin. */
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:\/\//i;

/** The `/api` suffix that `baseURL` carries but relative call sites omit. */
const API_PREFIX = /^\/api(?=\/|$)/;

/**
 * Valor booleano literal: el unico caso en el que `normalizeQuery` conserva el
 * VALOR de un parametro y no solo su clave.
 *
 * La regla es de FORMA, no de nombre: un booleano tiene un dominio de
 * exactamente dos valores, asi que nunca puede partir un issue en mas de dos
 * grupos, mientras que el texto libre de un buscador tiene dominio sin cota y
 * es justo la fragmentacion que este modulo vino a eliminar.
 *
 * A proposito NO se aceptan `1` ni `0`: por forma son indistinguibles de un
 * `offset=0` o un `limit=1` de paginacion, que son el caso opuesto (dominio sin
 * cota), y conservar el valor de un `offset` partiria el issue de un listado en
 * tantos grupos como paginas recorra el usuario. `true`/`false` no tienen esa
 * ambiguedad: es lo que axios serializa para un booleano de JS y lo que
 * `src/api/contracts.ts` escribe a mano.
 */
const BOOLEAN_VALUE = /^(?:true|false)$/i;

export const UNKNOWN_URL_FINGERPRINT = "unknown";

/**
 * Messages that mean "the client gave up on this request", in every spelling
 * they reach us with. Consumed only through `isAbortedRequestError` below, so
 * every caller goes through the same rule.
 *
 * Deliberately does NOT include timeouts: axios reports them with the same
 * `ECONNABORTED` code but a different message (`timeout of 5000ms exceeded` /
 * `timeout exceeded`), and a slow backend is real signal we want to keep.
 *
 * `cancell?ed` is ANCHORED on purpose. Its reach is still the whole app, even
 * though these patterns are no longer spread into the global `ignoreErrors`:
 * `beforeSend` runs `shouldIgnoreApiError` on EVERY exception, not only on API
 * ones, so this list is still matched against any error message that reaches
 * Sentry. `CANCELLED` is a first-class domain word here (order and membership
 * state machines), and an unanchored `/cancelled/i` would silently swallow
 * business errors such as `Order CANCELLED: payment failed`. The anchored form still
 * matches axios' own `CanceledError`, whose message is exactly `canceled`, and
 * real axios cancellations are detected by CODE anyway, before any message is
 * looked at.
 */
export const ABORTED_ERROR_PATTERNS: RegExp[] = [
  /request aborted/i,
  /the operation was aborted/i,
  /aborterror/i,
  /^cancell?ed$/i,
];

/** Kept from the previous behaviour: transport failures were already ignored. */
const NETWORK_ERROR_PATTERN = /network error/i;

/**
 * Preserved verbatim from the lists this module replaces. `timeout: 0` means
 * "never time out", so this message is a client misconfiguration rather than a
 * slow backend; axios 1.x does not even emit it any more (it says
 * `timeout exceeded` when `config.timeout` is falsy). Kept so the change does
 * not silently start reporting something that was muted before.
 */
const ZERO_TIMEOUT_PATTERN = /timeout of 0ms exceeded/i;

const isIdentifierSegment = (segment: string): boolean =>
  UUID_SEGMENT.test(segment) ||
  NUMERIC_SEGMENT.test(segment) ||
  LONG_HEX_SEGMENT.test(segment) ||
  OPAQUE_CODE_SEGMENT.test(segment);

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message ?? "";
  }
  if (typeof error === "string") {
    return error;
  }
  return "";
};

const getOrigin = (url: string): string => ORIGIN_PREFIX.exec(url)?.[0] ?? "";

/** Origin of the main API, used to tell "our backend" from other services. */
const API_ORIGIN = getOrigin(typeof API === "string" ? API : "");

/**
 * Query parameters reduced to a sorted, stable key. The VALUE survives only
 * when it is a boolean literal (`true` / `false`); every other value is
 * dropped and only its key is kept.
 *
 * Dropping values is the whole point of this module: search screens
 * interpolate the user's free text and ids into the URL (`?filter=<search
 * box>`) and listings paginate with `?limit=15&offset=30`, so keeping those
 * values opened one issue per keystroke and per page, and put PII in the issue
 * title.
 *
 * Keeping boolean values is the exception, and it is decided by SHAPE, never
 * by a list of known parameter names. A boolean has a domain of exactly two
 * values, so it can never fragment an issue beyond two groups, and in this
 * codebase these flags are the parameters that change what the backend does:
 * `?include_watermark=true` and `?include_watermark=false` render a different
 * document from the same endpoint (`src/api/contracts.ts`), and that flag is
 * sent on EVERY call to it, so collapsing it to the bare key filed a
 * watermark-only failure inside the issue of the normal download, where it may
 * raise no new alert at all. A boolean also carries no PII, so keeping it
 * costs nothing on that front either.
 *
 * A key sent several times keeps its values only when ALL of them are boolean;
 * otherwise the whole key collapses, so a repeated free-text parameter can
 * never leak one value through.
 */
const normalizeQuery = (query: string): string => {
  if (!query) {
    return "";
  }

  const valuesByKey = new Map<string, Set<string>>();

  Array.from(new URLSearchParams(query).entries()).forEach(([key, value]) => {
    if (key === "") {
      return;
    }

    const values = valuesByKey.get(key) ?? new Set<string>();
    values.add(value);
    valuesByKey.set(key, values);
  });

  const tokens = new Set<string>();

  valuesByKey.forEach((values, key) => {
    const isBoundedFlag = Array.from(values).every((value) =>
      BOOLEAN_VALUE.test(value),
    );

    if (!isBoundedFlag) {
      tokens.add(key);
      return;
    }

    // Lower-cased so `?x=TRUE` and `?x=true` never end up in two issues.
    values.forEach((value) => tokens.add(`${key}=${value.toLowerCase()}`));
  });

  const sorted = Array.from(tokens).sort();

  return sorted.length > 0 ? `?${sorted.join("&")}` : "";
};

/**
 * Turns a request URL into a stable grouping key.
 *
 * Normalisation is done by the SHAPE of each segment, never by its position:
 * this API puts literal segments exactly where other routes put an id
 * (`/v1/tenants/reports/new` vs `/v1/tenants/reports/<uuid>`), so any
 * positional rule would merge unrelated endpoints.
 *
 * Every step below fixes a fragmentation observed in production:
 * - the origin and the `/api` prefix, because the same endpoint is called with
 *   an absolute URL from one helper and a relative one from another (the host
 *   is kept when it is NOT the main API, so two services that happen to share a
 *   path never collapse into the same issue);
 * - the query string values, because search screens interpolate the user's free
 *   text into the URL (boolean flags keep their value: see `normalizeQuery`);
 * - the leading slash, because a few call sites omit it;
 * - the trailing slash, because `/v1/contracts` and `/v1/contracts/` coexist.
 */
export function normalizeUrlForFingerprint(url?: string | null): string {
  if (!url) {
    return UNKNOWN_URL_FINGERPRINT;
  }

  const withoutHash = url.split("#")[0];
  const queryStart = withoutHash.indexOf("?");
  const rawPath =
    queryStart === -1 ? withoutHash : withoutHash.slice(0, queryStart);
  const query = queryStart === -1 ? "" : withoutHash.slice(queryStart + 1);

  const origin = getOrigin(rawPath);
  // Basta con que la url traiga host y que ese host no sea el del API
  // principal. A proposito NO se exige ademas que `API_ORIGIN` no este vacio:
  // `VITE_APP_BASE_URL` se hornea en build time desde un secreto y puede quedar
  // vacia o relativa (`.env.template` la trae en blanco y `/api` es un valor
  // normal en un deploy con proxy same-origin). Con esa condicion de mas, en
  // ese deploy la guarda se apagaba sola: `API_ORIGIN` era `""`, a TODA url
  // absoluta se le quitaba el host y un 500 de
  // `api.zip-codes.multiburo.com.mx/v1/foo/123` caia en el mismo issue que uno
  // del API principal, que es exactamente el colapso entre servicios que esta
  // linea existe para evitar.
  //
  // Sin esa condicion no hay falso positivo posible: si el API es relativa,
  // nunca se la alcanza por url absoluta —todo el codigo la compone con
  // `${API}`, que entonces no lleva host—, asi que cualquier url con host es de
  // otro servicio.
  const isForeignOrigin = origin !== "" && origin !== API_ORIGIN;

  let path = rawPath.replace(ORIGIN_PREFIX, "");

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  path = path.replace(API_PREFIX, "");

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  if (path.length > 1) {
    path = path.replace(/\/+$/, "");
  }

  const normalizedPath =
    path === "/"
      ? "/"
      : path
          .split("/")
          .map((segment) => (isIdentifierSegment(segment) ? ":id" : segment))
          .join("/");

  // Only a foreign host is kept, and without its scheme, so that
  // `${API}/v1/x` and `/v1/x` (the same endpoint reached two ways) keep
  // collapsing together while another service's `/v1/x` never does.
  const hostPrefix = isForeignOrigin ? origin.replace(SCHEME_PREFIX, "") : "";

  return `${hostPrefix}${normalizedPath}${normalizeQuery(query)}`;
}

/**
 * Discriminator used when there is no HTTP status. Keeping the old literal
 * `"network"` here would merge three different failure modes of the same
 * endpoint (browser abort, transport failure and backend timeout) into a single
 * issue as soon as the URL stops fragmenting them. Digits are collapsed so
 * `timeout of 5000ms exceeded` and `timeout of 8000ms exceeded` still group.
 */
export function normalizeErrorMessageForFingerprint(
  message?: string | null,
): string {
  const normalized = (message ?? "").trim().replace(/\d+/g, "N");
  return normalized === "" ? "network" : normalized;
}

export interface ApiErrorFingerprintInput {
  method?: string | null;
  url?: string | null;
  status?: number | null;
  message?: string | null;
}

export function buildApiErrorFingerprint({
  method,
  url,
  status,
  message,
}: ApiErrorFingerprintInput): string[] {
  return [
    "api-error",
    method ? method.toUpperCase() : "unknown",
    normalizeUrlForFingerprint(url),
    status ? status.toString() : normalizeErrorMessageForFingerprint(message),
  ];
}

/**
 * True when the request was given up on by the client (user navigated away,
 * reloaded, closed the tab) rather than failing.
 *
 * Checks the error CODE first and only then the message, because substring
 * matching on messages is exactly what failed before. Note that
 * `ECONNABORTED` alone is NOT enough: axios uses it for timeouts too, so the
 * abort message is still required.
 */
export function isAbortedRequestError(error: unknown): boolean {
  if (axios.isCancel(error)) {
    return true;
  }

  if (axios.isAxiosError(error)) {
    // A response came back, so the client did not give up on the request.
    // Checked before any message match: that is the hole the previous lists had.
    if (error.response) {
      return false;
    }

    if (error.code === "ERR_CANCELED") {
      return true;
    }

    const isBrowserAbort =
      error.code === "ECONNABORTED" &&
      /request aborted/i.test(error.message ?? "");

    if (isBrowserAbort) {
      return true;
    }
  }

  const message = getErrorMessage(error);
  return ABORTED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Errors that should never become a Sentry issue: client-side aborts and
 * transport failures (the latter preserved from the previous behaviour).
 *
 * Timeouts are intentionally absent: they are captured and, thanks to the URL
 * normalisation, now collapse into a single issue per endpoint.
 *
 * NOTE on visibility: ignoring `Network Error` means a backend that closes the
 * connection, a proxy that kills the request or a deploy cutting in-flight
 * requests produces no issue at all. That is pre-existing behaviour, kept
 * deliberately out of scope here, but it is NOT the same as "we would see it
 * some other way".
 *
 * This is NOT the only reason an API error may go uncaptured: the other one is
 * `expectedBusinessResponses.ts`, a separate predicate that recognises
 * responses the flow expects (method + normalised path + exact status + a
 * discriminator on the BODY). It is deliberately a different module: the
 * invariant below —an error carrying an HTTP response is never dropped by a
 * message match— must keep holding here.
 */
export function shouldIgnoreApiError(error: unknown): boolean {
  // A real HTTP response means the request completed. Whatever the message
  // says, it was neither an abort nor a transport failure, so it must not be
  // dropped by a message match.
  if (axios.isAxiosError(error) && error.response) {
    return false;
  }

  if (isAbortedRequestError(error)) {
    return true;
  }

  const message = getErrorMessage(error);
  return (
    NETWORK_ERROR_PATTERN.test(message) || ZERO_TIMEOUT_PATTERN.test(message)
  );
}

export interface IgnoredApiErrorBreadcrumb {
  category: "api.ignored";
  message: string;
  level: "info";
  data: {
    url?: string;
    method?: string;
    code?: string;
    reason?: string;
  };
}

/**
 * Breadcrumb left behind when an API error is dropped instead of captured.
 *
 * Honest about its reach: for a request aborted by a hard navigation the page
 * is being torn down, so no later event will ever carry this breadcrumb. It is
 * useful for the failures that happen on a live page (transport failures, and
 * any in-page cancellation added later), not a substitute for capturing.
 *
 * `reason` overrides the default (the axios message) so an expected business
 * response can record WHICH rule recognised it (`expectedBusinessResponses.ts`
 * passes the rule id). The message of such an error is always the generic
 * `Request failed with status code 400`, which says nothing; the rule id says
 * everything and, unlike the response body, carries no user data.
 */
export function buildIgnoredApiErrorBreadcrumb(
  error: unknown,
  reason?: string,
): IgnoredApiErrorBreadcrumb {
  const axiosError = axios.isAxiosError(error) ? error : undefined;
  const url = axiosError?.config?.url;
  const code = axiosError?.code;

  return {
    category: "api.ignored",
    message: `${code || "ignored"} ${url ?? ""}`.trim(),
    level: "info",
    data: {
      url,
      method: axiosError?.config?.method?.toUpperCase(),
      code,
      reason: reason ?? getErrorMessage(error),
    },
  };
}
